import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, ImagePlus, Loader2, Bold, Italic, Underline, List, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { testid, sanitizeHtml } from "@/lib/editPaths";
import { ImageLinkDialog } from "@/components/ViaLinkImage";

export function Txt({ edit, path, value, as = "span", className, placeholder }) {
  const As = as;
  const tid = `editable-${testid(path)}`;
  if (!edit) return <As className={className} data-testid={tid} dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />;
  return (
    <As
      contentEditable
      suppressContentEditableWarning
      className={`${className || ""} cwz-editable`}
      data-placeholder={placeholder}
      data-testid={tid}
      onBlur={(e) => edit.set(path, sanitizeHtml(e.currentTarget.innerHTML))}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
    />
  );
}

export function FormatToolbar() {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) { setPos(null); return; }
      const node = sel.anchorNode?.nodeType === 1 ? sel.anchorNode : sel.anchorNode?.parentElement;
      if (!node?.closest?.(".cwz-editable")) { setPos(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (!rect.width && !rect.height) { setPos(null); return; }
      setPos({ top: rect.top - 46, left: rect.left + rect.width / 2 });
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);
  if (!pos) return null;
  const fmt = (cmd) => (e) => { e.preventDefault(); document.execCommand(cmd, false, null); };
  const btn = "w-7 h-7 rounded-md text-slate-300 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors";
  return (
    <div className="fixed z-[100] flex items-center gap-0.5 rounded-full bg-[#17263B] px-1.5 py-1 shadow-2xl -translate-x-1/2"
      style={{ top: Math.max(pos.top, 8), left: pos.left }} data-testid="format-toolbar">
      <button type="button" onMouseDown={fmt("bold")} className={btn} aria-label="Bold" data-testid="format-bold"><Bold className="w-3.5 h-3.5" /></button>
      <button type="button" onMouseDown={fmt("italic")} className={btn} aria-label="Italic" data-testid="format-italic"><Italic className="w-3.5 h-3.5" /></button>
      <button type="button" onMouseDown={fmt("underline")} className={btn} aria-label="Underline" data-testid="format-underline"><Underline className="w-3.5 h-3.5" /></button>
      <button type="button" onMouseDown={fmt("insertUnorderedList")} className={btn} aria-label="Bullet list" data-testid="format-list"><List className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function EditImage({ edit, path, value, wrapClass, imgClass, alt }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const tid = testid(path);

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload-image", fd);
      edit.set(path, data.url);
      toast.success("Image updated — save the page to publish it");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={`relative ${wrapClass || ""} ${edit ? "group" : ""}`} data-testid={`edit-image-${tid}`}>
      {value ? (
        <img src={value} alt={alt || ""} className={imgClass} loading="lazy" />
      ) : (
        <div className={`${imgClass || ""} bg-slate-200 flex items-center justify-center`}>
          <ImagePlus className="w-8 h-8 text-slate-400" />
        </div>
      )}
      {edit && (
        <>
          <div className="absolute inset-0 m-auto w-fit h-fit flex flex-col items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded-full bg-[#17263B]/90 text-white text-xs font-bold px-4 py-2.5 flex items-center gap-1.5 shadow-lg"
              data-testid={`edit-image-upload-${tid}`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : "Change image"}
            </button>
            <button
              type="button"
              onClick={() => setLinkOpen(true)}
              className="rounded-full bg-white/90 text-slate-700 text-[10px] font-bold px-3 py-1.5 flex items-center gap-1 shadow-lg hover:bg-white"
              data-testid={`edit-image-link-${tid}`}
            >
              <Link2 className="w-3 h-3" /> via link
            </button>
            {value && (
              <button
                type="button"
                onClick={() => { edit.set(path, ""); toast.success("Image removed — save the page to publish it"); }}
                className="rounded-full bg-white/90 text-red-500 text-[10px] font-bold px-3 py-1.5 flex items-center gap-1 shadow-lg hover:bg-white"
                data-testid={`edit-image-remove-${tid}`}
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`edit-image-input-${tid}`} />
          <ImageLinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} onSet={(u) => edit.set(path, u)}
            successMessage="Image updated — save the page to publish it" testid={`edit-image-link-dialog-${tid}`} />
        </>
      )}
    </div>
  );
}

export function ItemTools({ edit, path, index, count }) {
  if (!edit) return null;
  const tid = `item-tools-${testid(path)}-${index}`;
  return (
    <div className="absolute -left-2 top-0 -translate-x-full flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity max-lg:hidden" data-testid={tid}>
      <button type="button" onClick={() => edit.move(path, index, -1)} disabled={index === 0} aria-label="Move up"
        className="w-7 h-7 rounded-lg bg-[#17263B] text-white flex items-center justify-center disabled:opacity-30 hover:bg-[#0f1c2e]" data-testid={`${tid}-up`}>
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => edit.move(path, index, 1)} disabled={index === count - 1} aria-label="Move down"
        className="w-7 h-7 rounded-lg bg-[#17263B] text-white flex items-center justify-center disabled:opacity-30 hover:bg-[#0f1c2e]" data-testid={`${tid}-down`}>
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => edit.remove(path, index)} aria-label="Delete"
        className="w-7 h-7 rounded-lg bg-red-500 text-white flex items-center justify-center hover:bg-red-600" data-testid={`${tid}-remove`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function AddBtn({ edit, path, blank, label }) {
  if (!edit) return null;
  return (
    <button type="button" onClick={() => edit.add(path, blank)}
      className="inline-flex items-center gap-1.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-600 text-sm font-bold px-4 py-2.5 hover:bg-amber-50 transition-colors"
      data-testid={`add-${testid(path)}`}>
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}
